import React, { Component, ErrorInfo, ReactNode } from 'react';
import { EnhancedErrorBoundary } from './enhanced-error-boundary';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    // Global error reporting
    this.reportError(error, errorInfo);
  }

  private reportError = async (error: Error, errorInfo: ErrorInfo) => {
    try {
      // Log to external service
      const errorReport = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      console.error('Global error:', errorReport);
      
      // In production, send to monitoring service
      if (process.env.NODE_ENV === 'production') {
        // Send to error tracking service
      }
    } catch (reportError) {
      console.error('Failed to report error:', reportError);
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <EnhancedErrorBoundary
          onError={this.reportError}
          showDetails={process.env.NODE_ENV === 'development'}
        >
          <div>Fallback UI</div>
        </EnhancedErrorBoundary>
      );
    }

    return this.props.children;
  }
}